import { useMemo, useCallback } from "react";

import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { getUsDateFromIsoDate } from "@components/Date";
import { createBatchInvestorFilesOperation as createOperation } from "@api/services/backstage";

import type { Batch, InvestorFile } from "../types";

type BatchFile = Pick<InvestorFile, "batchId">;

type BatchOutput = {
  data: Batch;
};

type Options = {
  filesToMove: BatchFile[];
  sourceBatches: Batch[];
  targetBatches: Batch[];
  filesToOverride: BatchFile[];
  targetProjectId?: string;
  setTargetBatches: (batches: Batch[]) => void;
};

export type TargetBatches = {
  getTargetBatchId: (sourceBatchId: string) => string;
};

const useTargetBatches = ({
  filesToMove,
  sourceBatches,
  targetBatches,
  filesToOverride,
  targetProjectId,
  setTargetBatches,
}: Options) => {
  const { request } = useAppContext() as AppContext;

  const sourceBatchIds = useMemo(() =>
    [ ...filesToMove, ...filesToOverride ].map(({ batchId: sourceBatchId }) => sourceBatchId)
  , [ filesToMove, filesToOverride ]);

  const getSourceBatch = useCallback((sourceBatchId: string) =>
    sourceBatches.find(({ id }) => id === sourceBatchId)
  , [ sourceBatches ]);

  const getTargetBatchName = useCallback((sourceBatchId: string) => {
    const { name } = getSourceBatch(sourceBatchId);
    const usDate = getUsDateFromIsoDate(new Date());

    const targetBatchName = `${name} (Transferred on ${usDate})`;
    return targetBatchName;
  }, [
    getSourceBatch,
  ]);

  const createTargetBatch = useCallback(async (sourceBatchId: string) => {
    const name = getTargetBatchName(sourceBatchId);

    const {
      date,
      documentType,
    } = getSourceBatch(sourceBatchId);

    const isSent = true;

    const projectId = targetProjectId;
    const scheduledAt = undefined;
    const investorFiles: unknown[] = [];

    const mutation = {
      name,
      date,
      isSent,
      projectId,
      scheduledAt,
      documentType,
      investorFiles,
    };

    const { data: newBatch } = await request<BatchOutput>(createOperation, { mutation });

    return newBatch;
  }, [
    request,
    getSourceBatch,
    targetProjectId,
    getTargetBatchName,
  ]);

  const ensureTargetBatches = useCallback(async (): Promise<TargetBatches> => {
    const allTargetBatches = [ ...targetBatches ];

    const getTargetBatch = (sourceBatchId: string) => {
      const targetBatchName = getTargetBatchName(sourceBatchId);

      const targetBatch = allTargetBatches
        .find(({ name }) => name === targetBatchName);

      return targetBatch;
    };

    for (const sourceBatchId of sourceBatchIds) {
      const isTargetBatchExist = !!getTargetBatch(sourceBatchId);

      if (!isTargetBatchExist) {
        const createdBatch = await createTargetBatch(sourceBatchId);
        allTargetBatches.push(createdBatch);
      }
    }

    const getTargetBatchId = (sourceBatchId: string) => {
      const targetBatch = getTargetBatch(sourceBatchId);
      return targetBatch.id;
    };

    setTargetBatches(allTargetBatches);

    return {
      getTargetBatchId,
    };
  }, [
    targetBatches,
    sourceBatchIds,
    setTargetBatches,
    createTargetBatch,
    getTargetBatchName,
  ]);

  return {
    ensureTargetBatches,
  };
};

export default useTargetBatches;
